import { showErrorToast } from 'components/Toast/Toast';
import { ERR_NO_ORGS } from 'helpers/messages';
import { acceptInvites, getPendingInvites } from 'state_management/accounts/invite/api';
import { getUserOrganizationDetails } from 'state_management/accounts/user_organization_details/api';
import { OrganizationInfoResponse } from 'state_management/accounts/user_organization_details/responses';

export const handleAcceptInvite = async (authUserID?: string) => {
  try {
    // Check if URL is from invite email link [/organization/{ORG_ID}] and extract ORG_ID value
    const [, organizationID] = window.location.pathname.match(/^\/organization\/([a-f0-9]{24})$/) ?? [];

    // Get user's OrganizationWorkspaces and MultiOrganizationInvites in parallel
    const organizationsWorkspacesPromise = authUserID ? getUserOrganizationDetails(authUserID) : Promise.resolve<OrganizationInfoResponse>({ organizations: [] });
    const multiOrganizationInvitesPromise = getPendingInvites();
    const [organizationWorkspaces, multiOrganizationInvites] = await Promise.all([organizationsWorkspacesPromise, multiOrganizationInvitesPromise]);

    // Get count of user's organizations and invites
    const countOrganizations = organizationWorkspaces.organizations?.length ?? 0;
    const countInvites = multiOrganizationInvites.invites.length;

    logger.debug('Invites', { extra: multiOrganizationInvites });

    switch (countOrganizations) {
      case 0:
        switch (countInvites) {
          case 0: {
            /**
             * - New user (no orgs)
             * - No invites
             *   -> NOOP
             */
            logger.debug('AutoAccept :: New user, no invites -> NOOP');
            showErrorToast({ title: ERR_NO_ORGS, duration: 0 });
            break;
          }
          case 1: {
            /**
             * - New user (no orgs)
             * - Single invite
             *   -> Auto-accept single invite
             */
            logger.debug('AutoAccept :: New user, single invite -> Auto-accept invite');
            await acceptInvites(multiOrganizationInvites.invites);
            window.location.reload();
            break;
          }
          default: {
            /**
             * - New user (no orgs)
             * - Multiple invites
             *   - URL is invite email link and matching invite found
             *     -> Auto-accept matching invite
             *   - Otherwise
             *     -> Auto-accept all invites
             */
            const matchingInvite = multiOrganizationInvites.invites.find(({ orgId }) => orgId === organizationID);
            if (matchingInvite) {
              logger.debug('AutoAccept :: New user, multiple invites, matching invite found -> Auto-accept matching invite', { extra: matchingInvite });
              await acceptInvites([matchingInvite]);
            } else {
              logger.debug('AutoAccept :: New user, multiple invites, no matching invite found -> Auto-accept all invites');
              await acceptInvites(multiOrganizationInvites.invites);
            }
            window.location.reload();
            break;
          }
        }
        break;
      default:
        switch (countInvites) {
          case 0: {
            /**
             * - Existing user (multiple orgs)
             * - No invites
             *   -> NOOP
             */
            logger.debug('AutoAccept :: Existing user, no invites -> NOOP');
            break;
          }
          default: {
            /**
             * - Existing user (multiple orgs)
             * - Single or multiple invites
             *   - URL is invite email link and matching invite found
             *     -> Auto-accept matching invite
             *   - Otherwise
             *     -> NOOP
             */
            const organizationInvite = multiOrganizationInvites.invites.find(({ orgId }) => orgId === organizationID);
            if (organizationInvite) {
              logger.debug('AutoAccept :: Existing user, single/multiple invites, matching invite found -> Auto-accept matching invite', { extra: organizationInvite });
              await acceptInvites([organizationInvite]);
              window.location.reload();
            } else {
              logger.debug('AutoAccept :: Existing user, single/multiple invites, no matching invite found -> NOOP');
            }
            break;
          }
        }
        break;
    }
  } catch (error) {
    logger.error('An error occurred while trying to get/accept invites', {
      error
    });
  }
};
